import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { PLATFORM_AUTH_ERROR_OPTIONS } from "@/components/platform-admin/platform-login-form";
import { platformSetPassword } from "@/services/api/platform-auth";

/** MT-1 AUTH-009 `/platform/set-password?token=` (FR-6.9): the one-time link's page. */
export function PlatformSetPasswordPage() {
  const t = useTranslations("platformAuth");
  const tRoot = useTranslations();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      setError(t("errors.PLATFORM_LINK_INVALID"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await platformSetPassword(token, password);
      navigate("/platform/login?password=set", { replace: true });
    } catch (err) {
      setError(apiErrorText(err, tRoot, "platformAuth.failed", PLATFORM_AUTH_ERROR_OPTIONS));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-6 text-2xl font-semibold">{t("setPasswordTitle")}</h1>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="platform-set-password-form">
        <div>
          <label htmlFor="platform-new-password" className="mb-1 block text-sm font-medium">
            {t("newPasswordLabel")}
          </label>
          <input
            id="platform-new-password"
            data-testid="platform-new-password-input"
            type="password"
            autoComplete="new-password"
            aria-describedby="platform-password-rule"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
          <p id="platform-password-rule" className="mt-1 text-xs text-[var(--text-muted)]">
            {t("passwordRule")}
          </p>
        </div>
        {error && (
          <p
            role="alert"
            className="text-sm text-[var(--danger)]"
            data-testid="platform-set-password-error"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="platform-set-password-submit"
          className="w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
        >
          {t("savePassword")}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/platform/forgot-password" className="text-[var(--text-heading)] underline">
          {t("newLinkLink")}
        </Link>
      </p>
    </main>
  );
}
